/*
 * Network analytics for the intelligence workspace. Pure functions, no dependencies.
 * The graph is treated as undirected and weighted (weights default to 1).
 *
 * Measures and what they mean for an analyst:
 * - degree / strength: how many direct ties (and how strong)
 * - PageRank: influence through the network — tied to well-tied actors
 * - betweenness: brokerage — how often an actor sits on shortest paths between others
 * - Burt's constraint: low constraint = spans structural holes (access to separate groups)
 * - reach (2 hops): how many actors can be reached through one intermediary
 * - articulation points / bridges: single points of failure — removing them splits the network
 * - communities (Louvain): clusters of densely tied actors
 */

export interface AnalyticsEdge {
  source: string;
  target: string;
  weight?: number;
}

export interface Graph {
  ids: string[];
  index: Map<string, number>;
  /** neighbour index → accumulated weight */
  adj: Array<Map<number, number>>;
  /** neighbour lists as arrays for fast traversal */
  nbrs: number[][];
  strength: number[];
  totalWeight: number;
}

export function buildGraph(nodeIds: string[], edges: AnalyticsEdge[]): Graph {
  const ids = Array.from(new Set(nodeIds));
  const index = new Map<string, number>();
  ids.forEach((id, i) => index.set(id, i));
  const adj: Array<Map<number, number>> = ids.map(() => new Map());
  let totalWeight = 0;
  for (const e of edges) {
    const a = index.get(e.source);
    const b = index.get(e.target);
    if (a === undefined || b === undefined || a === b) continue;
    const w = e.weight && e.weight > 0 ? e.weight : 1;
    adj[a].set(b, (adj[a].get(b) || 0) + w);
    adj[b].set(a, (adj[b].get(a) || 0) + w);
    totalWeight += w;
  }
  const nbrs = adj.map((m) => Array.from(m.keys()));
  const strength = adj.map((m) => {
    let s = 0;
    m.forEach((w) => (s += w));
    return s;
  });
  return { ids, index, adj, nbrs, strength, totalWeight };
}

export const degree = (g: Graph): number[] => g.nbrs.map((n) => n.length);

export function density(g: Graph): number {
  const n = g.ids.length;
  if (n < 2) return 0;
  let edges = 0;
  g.nbrs.forEach((l) => (edges += l.length));
  return edges / 2 / ((n * (n - 1)) / 2);
}

/** Weighted PageRank; isolated nodes spread their rank evenly */
export function pageRank(g: Graph, damping = 0.85, iterations = 60): number[] {
  const n = g.ids.length;
  if (!n) return [];
  let rank = new Array(n).fill(1 / n);
  for (let it = 0; it < iterations; it++) {
    const next = new Array(n).fill((1 - damping) / n);
    let dangling = 0;
    for (let i = 0; i < n; i++) {
      if (g.strength[i] === 0) {
        dangling += rank[i];
        continue;
      }
      const share = (damping * rank[i]) / g.strength[i];
      g.adj[i].forEach((w, j) => {
        next[j] += share * w;
      });
    }
    const spread = (damping * dangling) / n;
    let delta = 0;
    for (let i = 0; i < n; i++) {
      next[i] += spread;
      delta += Math.abs(next[i] - rank[i]);
    }
    rank = next;
    if (delta < 1e-9) break;
  }
  return rank;
}

/**
 * Brandes betweenness (unweighted shortest paths), normalised to 0–1.
 * Large graphs use a deterministic sample of source nodes to stay interactive.
 */
export function betweenness(g: Graph, maxSources = 600): number[] {
  const n = g.ids.length;
  const cb = new Array(n).fill(0);
  if (n < 3) return cb;
  const step = n > maxSources ? n / maxSources : 1;
  const sources: number[] = [];
  for (let s = 0; s < n; s += step) sources.push(Math.floor(s));

  const sigma = new Float64Array(n);
  const dist = new Int32Array(n);
  const delta = new Float64Array(n);
  const preds: number[][] = Array.from({ length: n }, () => []);
  const queue = new Int32Array(n);
  const stack = new Int32Array(n);

  for (const s of sources) {
    sigma.fill(0);
    dist.fill(-1);
    delta.fill(0);
    for (let i = 0; i < n; i++) preds[i].length = 0;
    sigma[s] = 1;
    dist[s] = 0;
    let qh = 0;
    let qt = 0;
    let sp = 0;
    queue[qt++] = s;
    while (qh < qt) {
      const v = queue[qh++];
      stack[sp++] = v;
      for (const w of g.nbrs[v]) {
        if (dist[w] < 0) {
          dist[w] = dist[v] + 1;
          queue[qt++] = w;
        }
        if (dist[w] === dist[v] + 1) {
          sigma[w] += sigma[v];
          preds[w].push(v);
        }
      }
    }
    while (sp > 0) {
      const w = stack[--sp];
      for (const v of preds[w]) delta[v] += (sigma[v] / sigma[w]) * (1 + delta[w]);
      if (w !== s) cb[w] += delta[w];
    }
  }
  const scale = (step > 1 ? step : 1) / 2; // undirected pairs counted twice
  const norm = ((n - 1) * (n - 2)) / 2;
  return cb.map((v) => (v * scale) / norm);
}

/** Burt's constraint; lower means the actor bridges otherwise separate contacts. NaN for isolates. */
export function constraint(g: Graph): number[] {
  const n = g.ids.length;
  const p = (i: number, j: number) => {
    const s = g.strength[i];
    return s > 0 ? (g.adj[i].get(j) || 0) / s : 0;
  };
  const out = new Array(n).fill(NaN);
  for (let i = 0; i < n; i++) {
    if (!g.nbrs[i].length) continue;
    let c = 0;
    for (const j of g.nbrs[i]) {
      let indirect = 0;
      for (const q of g.nbrs[i]) {
        if (q === j) continue;
        indirect += p(i, q) * p(q, j);
      }
      c += (p(i, j) + indirect) ** 2;
    }
    out[i] = c;
  }
  return out;
}

/** Hop distances from `start` up to `maxHops` (start itself is 0) */
export function hopDistances(g: Graph, start: number, maxHops = Infinity): Map<number, number> {
  const dist = new Map<number, number>([[start, 0]]);
  let frontier = [start];
  let hop = 0;
  while (frontier.length && hop < maxHops) {
    hop++;
    const next: number[] = [];
    for (const v of frontier) {
      for (const w of g.nbrs[v]) {
        if (!dist.has(w)) {
          dist.set(w, hop);
          next.push(w);
        }
      }
    }
    frontier = next;
  }
  return dist;
}

export function reach(g: Graph, hops = 2): number[] {
  return g.ids.map((_, i) => hopDistances(g, i, hops).size - 1);
}

/** Connected components as arrays of node indices, largest first */
export function components(g: Graph): number[][] {
  const seen = new Uint8Array(g.ids.length);
  const out: number[][] = [];
  for (let i = 0; i < g.ids.length; i++) {
    if (seen[i]) continue;
    const comp: number[] = [];
    const stack = [i];
    seen[i] = 1;
    while (stack.length) {
      const v = stack.pop()!;
      comp.push(v);
      for (const w of g.nbrs[v]) {
        if (!seen[w]) {
          seen[w] = 1;
          stack.push(w);
        }
      }
    }
    out.push(comp);
  }
  return out.sort((a, b) => b.length - a.length);
}

/** Articulation points and bridges (iterative Tarjan, safe for deep graphs) */
export function cutStructure(g: Graph): { articulation: Set<number>; bridges: Array<[number, number]> } {
  const n = g.ids.length;
  const disc = new Int32Array(n).fill(-1);
  const low = new Int32Array(n);
  const parent = new Int32Array(n).fill(-1);
  const articulation = new Set<number>();
  const bridges: Array<[number, number]> = [];
  let time = 0;

  for (let root = 0; root < n; root++) {
    if (disc[root] !== -1) continue;
    let rootChildren = 0;
    const stack: Array<{ v: number; i: number }> = [{ v: root, i: 0 }];
    disc[root] = low[root] = time++;
    while (stack.length) {
      const frame = stack[stack.length - 1];
      const { v } = frame;
      if (frame.i < g.nbrs[v].length) {
        const w = g.nbrs[v][frame.i++];
        if (disc[w] === -1) {
          parent[w] = v;
          if (v === root) rootChildren++;
          disc[w] = low[w] = time++;
          stack.push({ v: w, i: 0 });
        } else if (w !== parent[v]) {
          low[v] = Math.min(low[v], disc[w]);
        }
      } else {
        stack.pop();
        const p = parent[v];
        if (p !== -1) {
          low[p] = Math.min(low[p], low[v]);
          if (low[v] > disc[p]) bridges.push([p, v]);
          if (p !== root && low[v] >= disc[p]) articulation.add(p);
        }
      }
    }
    if (rootChildren > 1) articulation.add(root);
  }
  return { articulation, bridges };
}

/** Louvain community detection; returns a community id per node (0 = largest community) */
export function louvain(g: Graph, maxLevels = 8): number[] {
  const n0 = g.ids.length;
  if (!n0) return [];
  const m2 = g.strength.reduce((a, b) => a + b, 0);
  let membership = g.ids.map((_, i) => i);
  if (m2 === 0) return renumberBySize(membership);

  // Level graph: adjacency without self loops + self-loop weights
  let level: LouvainLevel = { adj: g.adj.map((m) => new Map(m)), self: new Array(n0).fill(0) };
  for (let depth = 0; depth < maxLevels; depth++) {
    const comm = moveNodes(level, m2);
    if (!comm) break;
    const { next, remap } = aggregate(level, comm);
    membership = relabel(membership, comm, remap);
    const unchanged = next.adj.length === level.adj.length;
    level = next;
    if (unchanged) break;
  }
  return renumberBySize(membership);
}

interface LouvainLevel {
  adj: Array<Map<number, number>>;
  self: number[];
}

/** Local moving phase; returns the community per level node, or null when nothing moved */
function moveNodes({ adj, self }: LouvainLevel, m2: number): number[] | null {
  const n = adj.length;
  const k = adj.map((m, i) => {
    let s = 2 * self[i];
    m.forEach((w) => (s += w));
    return s;
  });
  const comm = Array.from({ length: n }, (_, i) => i);
  const tot = k.slice();
  let movedAny = false;

  for (let pass = 0; pass < 25; pass++) {
    let moved = false;
    for (let i = 0; i < n; i++) {
      const target = bestCommunity(i, adj[i], comm, tot, k[i], m2);
      if (target !== comm[i]) {
        comm[i] = target;
        moved = true;
        movedAny = true;
      }
    }
    if (!moved) break;
  }
  return movedAny ? comm : null;
}

function bestCommunity(i: number, neighbours: Map<number, number>, comm: number[], tot: number[], ki: number, m2: number): number {
  const ci = comm[i];
  const weights = new Map<number, number>();
  neighbours.forEach((w, j) => weights.set(comm[j], (weights.get(comm[j]) || 0) + w));
  tot[ci] -= ki;
  let best = ci;
  let bestGain = (weights.get(ci) || 0) - (tot[ci] * ki) / m2;
  weights.forEach((w, c) => {
    const gain = w - (tot[c] * ki) / m2;
    if (gain > bestGain + 1e-12) {
      bestGain = gain;
      best = c;
    }
  });
  tot[best] += ki;
  return best;
}

function aggregate({ adj, self }: LouvainLevel, comm: number[]): { next: LouvainLevel; remap: Map<number, number> } {
  const remap = new Map<number, number>();
  comm.forEach((c) => {
    if (!remap.has(c)) remap.set(c, remap.size);
  });
  const size = remap.size;
  const nextAdj: Array<Map<number, number>> = Array.from({ length: size }, () => new Map());
  const nextSelf = new Array(size).fill(0);
  adj.forEach((neighbours, i) => {
    const ci = remap.get(comm[i])!;
    nextSelf[ci] += self[i];
    neighbours.forEach((w, j) => {
      const cj = remap.get(comm[j])!;
      if (ci === cj) {
        if (i < j) nextSelf[ci] += w;
      } else {
        nextAdj[ci].set(cj, (nextAdj[ci].get(cj) || 0) + w);
      }
    });
  });
  return { next: { adj: nextAdj, self: nextSelf }, remap };
}

function relabel(membership: number[], comm: number[], remap: Map<number, number>): number[] {
  return membership.map((c) => remap.get(comm[c])!);
}

function renumberBySize(membership: number[]): number[] {
  const counts = new Map<number, number>();
  membership.forEach((c) => counts.set(c, (counts.get(c) || 0) + 1));
  const order = Array.from(counts.keys()).sort((a, b) => counts.get(b)! - counts.get(a)! || a - b);
  const remap = new Map(order.map((c, i) => [c, i]));
  return membership.map((c) => remap.get(c)!);
}

/** Newman modularity of a partition (−0.5 … 1; > 0.3 means clear clustering) */
export function modularity(g: Graph, membership: number[]): number {
  const m2 = g.strength.reduce((a, b) => a + b, 0);
  if (!m2) return 0;
  const internal = new Map<number, number>();
  const totals = new Map<number, number>();
  for (let i = 0; i < g.ids.length; i++) {
    const c = membership[i];
    totals.set(c, (totals.get(c) || 0) + g.strength[i]);
    g.adj[i].forEach((w, j) => {
      if (membership[j] === c) internal.set(c, (internal.get(c) || 0) + w);
    });
  }
  let q = 0;
  totals.forEach((t, c) => {
    q += (internal.get(c) || 0) / m2 - (t / m2) ** 2;
  });
  return q;
}

/** Shortest path (fewest hops) between two nodes, as node indices, or null */
export function shortestPath(g: Graph, from: number, to: number, blocked?: Set<string>): number[] | null {
  if (from === to) return [from];
  const prev = new Int32Array(g.ids.length).fill(-1);
  const seen = new Uint8Array(g.ids.length);
  seen[from] = 1;
  const queue = [from];
  let head = 0;
  while (head < queue.length) {
    const v = queue[head++];
    for (const w of g.nbrs[v]) {
      if (seen[w]) continue;
      if (blocked && (blocked.has(`n:${w}`) || blocked.has(edgeKey(v, w)))) continue;
      seen[w] = 1;
      prev[w] = v;
      if (w === to) {
        const path = [to];
        let cur = to;
        while (prev[cur] !== -1) {
          cur = prev[cur];
          path.push(cur);
        }
        return path.reverse();
      }
      queue.push(w);
    }
  }
  return null;
}

const edgeKey = (a: number, b: number) => (a < b ? `e:${a}-${b}` : `e:${b}-${a}`);

/** Up to `k` loopless shortest paths (Yen's algorithm on hop count) */
export function kShortestPaths(g: Graph, from: number, to: number, k = 3): number[][] {
  const first = shortestPath(g, from, to);
  if (!first) return [];
  const found: number[][] = [first];
  const candidates: number[][] = [];
  const key = (p: number[]) => p.join('>');

  for (let ki = 1; ki < k; ki++) {
    const last = found[ki - 1];
    for (let i = 0; i < last.length - 1; i++) {
      const spur = last[i];
      const root = last.slice(0, i + 1);
      const blocked = new Set<string>();
      for (const p of found) {
        if (p.length > i && key(p.slice(0, i + 1)) === key(root)) blocked.add(edgeKey(p[i], p[i + 1]));
      }
      root.slice(0, -1).forEach((v) => blocked.add(`n:${v}`));
      const spurPath = shortestPath(g, spur, to, blocked);
      if (!spurPath) continue;
      const total = [...root.slice(0, -1), ...spurPath];
      if (!candidates.some((c) => key(c) === key(total)) && !found.some((c) => key(c) === key(total))) {
        candidates.push(total);
      }
    }
    if (!candidates.length) break;
    candidates.sort((a, b) => a.length - b.length);
    found.push(candidates.shift()!);
  }
  return found;
}

export function commonNeighbours(g: Graph, a: number, b: number): number[] {
  const set = new Set(g.nbrs[a]);
  return g.nbrs[b].filter((x) => set.has(x));
}

/** Percentile rank (0–100) of each value among all values; NaN stays NaN */
export function percentiles(values: number[], invert = false): number[] {
  const valid = values.filter((v) => Number.isFinite(v)).sort((a, b) => a - b);
  if (!valid.length) return values.map(() => NaN);
  return values.map((v) => {
    if (!Number.isFinite(v)) return NaN;
    let lo = 0;
    let hi = valid.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (valid[mid] < v) lo = mid + 1;
      else hi = mid;
    }
    let below = lo;
    let equal = 0;
    while (below + equal < valid.length && valid[below + equal] === v) equal++;
    const pct = valid.length === 1 ? 100 : ((below + equal / 2) / valid.length) * 100;
    return Math.round(invert ? 100 - pct : pct);
  });
}

/* ------------------------------------------------------------------ */

export type StructuralRole = 'hub' | 'broker' | 'gatekeeper' | 'connector' | 'peripheral' | 'isolate';

export const ROLE_LABEL: Record<StructuralRole, string> = {
  hub: 'Hub',
  broker: 'Broker',
  gatekeeper: 'Gatekeeper',
  connector: 'Connector',
  peripheral: 'Peripheral',
  isolate: 'Isolated',
};

export const ROLE_DESCRIPTION: Record<StructuralRole, string> = {
  hub: 'Highly connected to well-connected actors — central to influence in this network',
  broker: 'Sits between groups that are otherwise weakly tied — controls information flow',
  gatekeeper: 'Removing this actor would split the network — a single point of access',
  connector: 'Many direct ties, mostly inside one group',
  peripheral: 'Few ties, at the edge of the network',
  isolate: 'No recorded ties',
};

export interface NodeMetrics {
  id: string;
  degree: number;
  strength: number;
  pagerank: number;
  betweenness: number;
  constraint: number;
  reach2: number;
  community: number;
  component: number;
  isArticulation: boolean;
  role: StructuralRole;
  /** 0–100 percentile ranks within the analysed network */
  pct: { influence: number; brokerage: number; degree: number; reach: number };
}

export interface Community {
  id: number;
  members: string[];
  /** Highest-PageRank members first */
  leaders: string[];
  internalTies: number;
  externalTies: number;
  /** internal ties / possible ties */
  cohesion: number;
}

export interface NetworkAnalysis {
  graph: Graph;
  metrics: Map<string, NodeMetrics>;
  communities: Community[];
  modularity: number;
  density: number;
  components: number;
  largestComponentShare: number;
  articulation: string[];
  bridges: Array<[string, string]>;
  isolates: string[];
  ranked: {
    influence: string[];
    brokerage: string[];
    degree: string[];
    reach: string[];
  };
}

export function analyzeNetwork(nodeIds: string[], edges: AnalyticsEdge[]): NetworkAnalysis {
  const g = buildGraph(nodeIds, edges);
  const n = g.ids.length;
  const deg = degree(g);
  const pr = pageRank(g);
  const bc = betweenness(g);
  const con = constraint(g);
  const r2 = reach(g, 2);
  const comms = louvain(g);
  const comps = components(g);
  const compOf = new Array(n).fill(0);
  comps.forEach((c, ci) => c.forEach((v) => (compOf[v] = ci)));
  const { articulation, bridges } = cutStructure(g);

  const pInfluence = percentiles(pr);
  const pBrokerage = percentiles(bc);
  const pDegree = percentiles(deg);
  const pReach = percentiles(r2);

  const metrics = new Map<string, NodeMetrics>();
  for (let i = 0; i < n; i++) {
    let role: StructuralRole;
    if (deg[i] === 0) role = 'isolate';
    else if (articulation.has(i) && pBrokerage[i] >= 75) role = 'gatekeeper';
    else if (pBrokerage[i] >= 85 && con[i] < 0.5) role = 'broker';
    else if (pInfluence[i] >= 85) role = 'hub';
    else if (pDegree[i] >= 70) role = 'connector';
    else role = 'peripheral';
    metrics.set(g.ids[i], {
      id: g.ids[i],
      degree: deg[i],
      strength: g.strength[i],
      pagerank: pr[i],
      betweenness: bc[i],
      constraint: con[i],
      reach2: r2[i],
      community: comms[i],
      component: compOf[i],
      isArticulation: articulation.has(i),
      role,
      pct: { influence: pInfluence[i], brokerage: pBrokerage[i], degree: pDegree[i], reach: pReach[i] },
    });
  }

  const byCommunity = new Map<number, number[]>();
  comms.forEach((c, i) => {
    if (deg[i] === 0) return; // isolates are not a cluster
    const list = byCommunity.get(c) || [];
    list.push(i);
    byCommunity.set(c, list);
  });
  const communities: Community[] = [];
  byCommunity.forEach((members, id) => {
    if (members.length < 2) return;
    const set = new Set(members);
    let internal = 0;
    let external = 0;
    members.forEach((v) =>
      g.nbrs[v].forEach((w) => {
        if (set.has(w)) internal += 0.5;
        else external += 1;
      }),
    );
    const possible = (members.length * (members.length - 1)) / 2;
    communities.push({
      id,
      members: members.map((v) => g.ids[v]),
      leaders: members
        .slice()
        .sort((a, b) => pr[b] - pr[a])
        .slice(0, 3)
        .map((v) => g.ids[v]),
      internalTies: internal,
      externalTies: external,
      cohesion: possible ? internal / possible : 0,
    });
  });
  communities.sort((a, b) => b.members.length - a.members.length);

  const rank = (values: number[]) =>
    g.ids
      .map((id, i) => [id, values[i]] as const)
      .filter(([, v]) => Number.isFinite(v) && v > 0)
      .sort((a, b) => b[1] - a[1])
      .map(([id]) => id);

  return {
    graph: g,
    metrics,
    communities,
    modularity: modularity(g, comms),
    density: density(g),
    components: comps.filter((c) => c.length > 1).length,
    largestComponentShare: n ? comps[0].length / n : 0,
    articulation: Array.from(articulation).map((i) => g.ids[i]),
    bridges: bridges.map(([a, b]) => [g.ids[a], g.ids[b]] as [string, string]),
    isolates: g.ids.filter((_, i) => deg[i] === 0),
    ranked: { influence: rank(pr), brokerage: rank(bc), degree: rank(deg), reach: rank(r2) },
  };
}

/** Paths between two entity ids, as id arrays */
export function pathsBetween(analysis: NetworkAnalysis, fromId: string, toId: string, k = 3): string[][] {
  const g = analysis.graph;
  const a = g.index.get(fromId);
  const b = g.index.get(toId);
  if (a === undefined || b === undefined) return [];
  return kShortestPaths(g, a, b, k).map((p) => p.map((i) => g.ids[i]));
}

/** Entities within `hops` of an entity, with their distance */
export function egoNetwork(analysis: NetworkAnalysis, id: string, hops = 2): Map<string, number> {
  const g = analysis.graph;
  const i = g.index.get(id);
  if (i === undefined) return new Map();
  const out = new Map<string, number>();
  hopDistances(g, i, hops).forEach((d, v) => out.set(g.ids[v], d));
  return out;
}

export function sharedConnections(analysis: NetworkAnalysis, aId: string, bId: string): string[] {
  const g = analysis.graph;
  const a = g.index.get(aId);
  const b = g.index.get(bId);
  if (a === undefined || b === undefined) return [];
  return commonNeighbours(g, a, b).map((i) => g.ids[i]);
}

/** Herfindahl–Hirschman concentration of a set of shares (0–10,000) */
export function hhi(values: number[]): number {
  const total = values.reduce((a, b) => a + Math.max(0, b), 0);
  if (!total) return 0;
  return Math.round(values.reduce((acc, v) => acc + ((Math.max(0, v) / total) * 100) ** 2, 0));
}

/** Plain-language reading of an HHI value (thresholds from market-concentration practice) */
export function concentrationLabel(value: number): 'Dominated' | 'Concentrated' | 'Competitive' | 'Fragmented' {
  if (value >= 4000) return 'Dominated';
  if (value >= 2500) return 'Concentrated';
  if (value >= 1500) return 'Competitive';
  return 'Fragmented';
}
